import { CommentList } from '../CommentDiscussion';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import useDraft from '../../hooks/useDraft';
import useBackLayer from '../../hooks/useBackLayer';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { Heart, Star, Share2, MessageCircle, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRating } from '../../context/RatingContext';
import { useComments } from '../../context/CommentsContext';
import { useShelf } from '../../context/ShelfContext';
import { useWishlist } from '../../context/WishlistContext';
import { useUser } from '../../context/UserContext';
import { useFriends } from '../../context/FriendsContext';
import { getFriendsWithItem, getSimilarByCategory, getMovieDetails, getTVShowDetails, getGameDetails, getBookDetails, lookupMusic, fetchWithAuth } from '../../services/api';

const PanelContainer = styled.aside`
  width: 320px;
  background: transparent;
  border-left: 0px solid rgba(255, 255, 255, 0.08);
  height: calc(100vh - 64px);
  position: fixed;
  right: 0;
  top: 64px;
  padding: 24px;
  overflow-y: auto;
  z-index: 100;
  color: ${({ theme }) => theme.colors.text};
  transition: background-color 0.3s ease, border-color 0.3s ease;

  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 1200px) { width: 280px; }
  @media (max-width: 1024px) { display: none; }
`;

const Section = styled.section`
  margin-bottom: 24px;
  padding: 20px;
  border-radius: 20px;
  background: ${({ theme }) => theme.isDarkMode ? '#18191B' : '#EFF3F7'};
  transition: background-color 0.3s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PolkarateIcon = styled.img`
  width: 24px;
  height: 24px;
  filter: ${({ theme }) => theme.isDarkMode ? 'none' : 'brightness(0.09)'};
`;

const RatingValue = styled.div`
  font-size: 40px;
  font-weight: 800;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
  line-height: 1;
`;

const SmallText = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.72)' : '#18191B'};
  font-size: 13px;
`;

const StarsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 10px 0 6px;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  margin-top: 14px;
  height: 70px;
`;

const ProgressItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  gap: 6px;
`;

const ProgressBarVert = styled.div`
  width: 100%;
  max-width: 16px;
  max-height: 52px;
  flex: 1;
  border-radius: 4px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(24, 25, 27, 0.08)'};
  position: relative;
  overflow: hidden;
  align-self: center;
`;

const ProgressFillVert = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 4px;
  background: ${({ theme }) => theme.isDarkMode ? '#EFF3F7' : '#18191B'};
  transition: height 0.3s ease;
`;

const ProgressLabel = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(24, 25, 27, 0.5)'};
  font-size: 10px;
  line-height: 1;
  text-align: center;
`;

const ActionIconImg = styled.img`
  width: 16px;
  height: 16px;
  filter: ${({ theme }) => theme.isDarkMode
    ? 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
    : 'brightness(0) saturate(100%) invert(9%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'};
  transition: filter 0.2s ease;
`;

const ActionButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
`;


const FriendList = styled.div`
  display: grid;
  gap: 12px;
`;

const FriendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FriendAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const FriendMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FriendName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
`;

const FriendStatus = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#18191B'};
`;

const SimilarCard = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-radius: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const SimilarCover = styled.img`
  width: 60px;
  height: 84px;
  border-radius: 14px;
  object-fit: cover;
`;

const SimilarInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SimilarTitle = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
  font-size: 14px;
  font-weight: 700;
`;

const SimilarMeta = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#18191B'};
  font-size: 12px;
`;

const ReviewItem = styled.div`
  display: grid;
  gap: 12px;
  padding: 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ReviewAuthorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReviewAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const ReviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReviewName = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
  font-weight: 700;
  font-size: 14px;
`;

const ReviewDate = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#18191B'};
  font-size: 12px;
`;

const ReviewRating = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.72)' : '#18191B'};
  font-size: 13px;
  white-space: nowrap;
`;

const Placeholder = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#18191B'};
  font-size: 14px;
`;

const RatingModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2200;
`;
const DetailRightPanel = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { category, id } = useParams();
  const navigate = useNavigate();
  const { addToShelf, removeFromShelf, isInShelf } = useShelf();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { getAverageRating, getRatingCount, getRatingDistribution, getUserRating, setUserRating, fetchRatingDistribution } = useRating();
  const { getCommentsForItem, loadComments, addComment } = useComments();
  const { user } = useUser();

  const item = useMemo(() => location.state?.item || { id, category }, [location.state?.item, id, category]);
  const apiId = useMemo(() => item?.external_id || item?.id || id, [item?.external_id, item?.id, id]);
  const itemCategory = useMemo(() => category || item?.category || 'film', [category, item?.category]);
  const itemTitle = item?.title || item?.name || t('common.project');
  const inShelf = isInShelf(apiId, itemCategory);
  const inWishlist = isInWishlist(apiId, itemCategory);

  // Загружаем детали, чтобы получить корректные жанры (с названиями)
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!apiId || !itemCategory) return;
    
    // Если жанры уже есть в item — не загружаем
    const existingGenres = item?.genres || [];
    const hasGenres = existingGenres.length > 0 && existingGenres.some(g => {
      if (typeof g === 'object' && g !== null) return !!g.name;
      if (typeof g === 'string') return !!g.trim();
      return false;
    });
    if (hasGenres) return;

    const loadDetails = async () => {
      try {
        let data = null;
        switch (itemCategory) {
          case 'film':
            data = await getMovieDetails(apiId);
            break;
          case 'series':
            data = await getTVShowDetails(apiId);
            break;
          case 'game':
            data = await getGameDetails(apiId);
            break;
          case 'music': {
            const musicData = await lookupMusic(apiId, 'RU');
            const lookupItem = musicData?.results?.[0] || {};
            data = {
              genres: lookupItem.primaryGenreName ? [lookupItem.primaryGenreName] : []
            };
            break;
          }
          case 'book': {
            data = await getBookDetails(apiId);
            break;
          }
        }
        if (data) setDetails(data);
      } catch (err) {
        console.warn('Ошибка загрузки деталей для жанров:', err);
      }
    };
    loadDetails();
  }, [apiId, itemCategory, item]);

  // Собираем жанры из item (из state) или из загруженных details
  const itemGenres = useMemo(() => {
    // Сначала пробуем из загруженных details (там genres с {name})
    if (details?.genres && Array.isArray(details.genres) && details.genres.length > 0) {
      // Для книг categories — это строки, для остальных — [{name: ...}]
      if (typeof details.genres[0] === 'string') return details.genres;
      const names = details.genres.map(g => typeof g === 'object' && g !== null ? (g.name || '') : g).filter(Boolean);
      if (names.length > 0) return names;
    }
    // Для музыки primaryGenreName
    if (details?.primaryGenreName) return [details.primaryGenreName];
    // Для книг categories
    if (details?.categories && Array.isArray(details.categories)) return details.categories.filter(Boolean);
    // Fallback на item из state
    const fromItem = item?.genres || [];
    return fromItem.map(g => typeof g === 'object' && g !== null ? (g.name || '') : g).filter(Boolean);
  }, [details, item]);
  
  const genreNames = itemGenres;

  const itemForSave = {
    id: apiId,
    external_id: apiId,
    category: itemCategory,
    title: itemTitle,
    poster: item?.poster || item?.artwork || '/assets/placeholder.png',
    artwork: item?.artwork || item?.poster || '/assets/placeholder.png',
    backdrop: item?.backdrop || null,
    originalTitle: item?.originalTitle || item?.original_title || undefined,
    overview: item?.overview || item?.description || undefined,
    releaseDate: item?.releaseDate || item?.release_date || undefined,
    genres: genreNames
  };

  const rating = getAverageRating(apiId, itemCategory);
  const ratingCount = getRatingCount(apiId, itemCategory);
  const ratingDistribution = getRatingDistribution(apiId, itemCategory);
  const currentComments = useMemo(() => getCommentsForItem(itemCategory, apiId).slice(0, 2), [itemCategory, apiId, getCommentsForItem]);
  const [friendsWithItem, setFriendsWithItem] = useState([]);
  const [similarItems, setSimilarItems] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { friends } = useFriends();
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
  const [newReviewText, setNewReviewText, draftSaved] = useDraft(`review:${itemCategory}:${apiId}`);
  const [reviewSubmitting, setReviewSubmitting] = useState(false), [reviewError, setReviewError] = useState('');
  const reviewLock = useRef(false);
  useBackLayer(() => setIsReviewsModalOpen(false), isReviewsModalOpen);

  useEffect(() => {
    if (!user || !apiId || !itemCategory) return;
    const loadFriends = async () => {
      try {
        const result = await getFriendsWithItem(apiId, itemCategory);
        setFriendsWithItem(Array.isArray(result) ? result.slice(0, 4) : []);
      } catch (err) {
        console.warn(t('common.error'), err);
      }
    };
    loadFriends();
  }, [apiId, itemCategory, user, t]);

  useEffect(() => {
    const loadSimilar = async () => {
      try {
        const results = await getSimilarByCategory(itemCategory, apiId, 5);
        if (Array.isArray(results)) {
          const filtered = results.filter(similar => similar.id !== apiId).slice(0, 3);
          setSimilarItems(filtered);
        }
      } catch (err) {
        console.warn(t('common.error'), err);
      }
    };
    loadSimilar();
  }, [apiId, itemCategory, t]);

  useEffect(() => {
    if (itemCategory && apiId) {
      loadComments(itemCategory, apiId);
    }
  }, [itemCategory, apiId, loadComments]);

  useEffect(() => {
    if (apiId && itemCategory) {
      fetchRatingDistribution(apiId, itemCategory);
    }
  }, [apiId, itemCategory, fetchRatingDistribution]);

  const pluralizeRating = (count) => {
    if (i18n.language === 'en') {
      return count === 1 ? 'rating' : 'ratings';
    }
    if (count === 1) return 'оценка';
    if (count >= 2 && count <= 4) return 'оценки';
    return 'оценок';
  };

  const handleRateSelect = async (value) => {
    try {
      await setUserRating(apiId, itemCategory, value);
    } catch (error) {
      alert(error.message || 'Не удалось сохранить оценку');
    }
  };

  const onShelfToggle = () => {
    if (inShelf) {
      removeFromShelf(apiId, itemCategory);
    } else {
      addToShelf(itemForSave);
    }
  };

  const onWishlistToggle = () => {
    if (inWishlist) {
      removeFromWishlist(apiId, itemCategory);
    } else {
      addToWishlist(itemForSave);
    }
  };

  const onShare = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setSearchQuery('');
  };

  const handleSelectFriend = async (friend) => {
    try {
      const notification = {
        recipientId: friend.id,
        senderId: user?.id || 0,
        type: 'share',
        content: `${user?.name || t('common.user')} ${t('common.sharedWithYou')}: ${itemTitle}`,
        itemId: apiId,
        itemCategory: itemCategory,
        itemTitle: itemTitle,
        itemPoster: itemForSave.poster,
        createdAt: new Date().toISOString(),
        read: false
      };

      await fetchWithAuth('/notifications', {
        method: 'POST',
        body: JSON.stringify(notification)
      });

      handleCloseShareModal();
    } catch (error) {
      console.error(t('common.error'), error);
    }
  };

  const determinePercent = (value) => {
    const dist = ratingDistribution;
    if (!Array.isArray(dist) || dist.length !== 11) return 1;
    const total = dist.reduce((sum, count) => sum + (Number(count) || 0), 0);
    if (!total) return 1;
    const idx = 10 - value;
    const count = Number(dist[idx]) || 0;
    return Math.max(1, Math.round((count / total) * 100));
  };

  const openReviewsModal = () => {
    setIsReviewsModalOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!newReviewText.trim() || reviewLock.current) return;
    reviewLock.current = true; setReviewSubmitting(true); setReviewError('');
    try {
      await addComment(itemCategory, apiId, newReviewText.trim());
      setNewReviewText(value => value === newReviewText ? '' : value);
    } catch (error) {
      setReviewError(error.message || t('ux.saveFailed'));
    } finally {
      reviewLock.current = false; setReviewSubmitting(false);
    }
  };

  return (
    <PanelContainer>
      <Section>
        <SectionHeader>
          <SectionTitle>{t('rightPanel.ratingPolka')}</SectionTitle>
        </SectionHeader>
        <RatingRow>
          <RatingValue>{rating !== null && rating !== undefined ? rating : '—'}</RatingValue>
          <PolkarateIcon src="/assets/polkarate.svg" alt={t('app.name')} />
        </RatingRow>
        <SmallText style={{ marginTop: 4, marginBottom: 8 }}>{t('rightPanel.averageRatingLabel')}</SmallText>
        <StarsRow>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => {
            const userR = getUserRating(apiId, itemCategory);
            const isFilled = userR != null ? userR >= value : rating >= value;
            return (
              <button
                key={value}
                onClick={() => handleRateSelect(value)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                aria-label={`${value}/10`}
              >
                <Star
                  size={16}
                  fill={isFilled ? '#F6CD54' : 'none'}
                  stroke="#F6CD54"
                  strokeWidth={2}
                  color="#F6CD54"
                />
              </button>
            );
          })}
        </StarsRow>
        <SmallText>
          {ratingCount
            ? `${ratingCount} ${pluralizeRating(ratingCount)}`
            : t('rightPanel.noRatings')}
        </SmallText>
        {getUserRating(apiId, itemCategory) != null && (
          <SmallText style={{ marginTop: 4 }}>{t('itemDetail.youRated', { rating: getUserRating(apiId, itemCategory) })}</SmallText>
        )}
        <ProgressRow>
          {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map(value => (
            <ProgressItem key={value}>
              <ProgressBarVert>
                <ProgressFillVert style={{ height: `${determinePercent(value)}%` }} />
              </ProgressBarVert>
              <ProgressLabel>{value}</ProgressLabel>
            </ProgressItem>
          ))}
        </ProgressRow>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>{t('rightPanel.actions')}</SectionTitle>
        </SectionHeader>
        <ActionButton onClick={onShelfToggle}>
          <ActionIconImg src="/polkamenuicon.png" alt={t('rightPanel.addToShelf')} />
          {inShelf ? t('rightPanel.removeFromShelf') : t('rightPanel.addToShelf')}
        </ActionButton>
        <ActionButton onClick={onWishlistToggle}>
          <Heart size={16} /> {inWishlist ? t('rightPanel.removeFromWishlist') : t('rightPanel.addToWishlist')}
        </ActionButton>
        <ActionButton onClick={openReviewsModal}>
          <MessageCircle size={16} /> {t('rightPanel.review')}
        </ActionButton>
        <ActionButton onClick={onShare}>
          <Share2 size={16} /> {t('rightPanel.share')}
        </ActionButton>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>{t('rightPanel.friendsWithItem')}</SectionTitle>
        </SectionHeader>
        {friendsWithItem.length > 0 ? (
          <FriendList>
            {friendsWithItem.map(friend => (
              <FriendItem key={friend.id || friend.user_id || friend.username}>
                <FriendAvatar src={friend.avatar || '/assets/avatar-placeholder.png'} alt={friend.name || friend.username || t('rightPanel.friend')} />
                <FriendMeta>
                  <FriendName>{friend.name || friend.username || t('rightPanel.friend')}</FriendName>
                  <FriendStatus>{friend.status || t('rightPanel.wasOnline')}</FriendStatus>
                </FriendMeta>
              </FriendItem>
            ))}
          </FriendList>
        ) : (
          <Placeholder>{t('rightPanel.noFriendsWithItem')}</Placeholder>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>{t('rightPanel.similar')}</SectionTitle>
        </SectionHeader>
        {similarItems.length > 0 ? similarItems.map((similar, index) => (
          <SimilarCard
            key={similar.id || `${similar.title || 'item'}-${index}`}
            onClick={() => {
              const similarCategory = similar.category || itemCategory;
              navigate(`/item/${similarCategory}/${similar.id}`, {
                state: { item: { id: similar.id, external_id: similar.id, title: similar.title || similar.name, category: similarCategory, poster: similar.poster || similar.artwork || null, artwork: similar.artwork || similar.poster || null } }
              });
            }}
          >
            <SimilarCover src={similar.poster || similar.artwork || '/assets/placeholder.png'} alt={similar.title} />
            <SimilarInfo>
              <SimilarTitle>{similar.title || similar.name || t('rightPanel.project')}</SimilarTitle>
              <SimilarMeta>{similar.releaseDate ? new Date(similar.releaseDate).getFullYear() : similar.first_air_date ? new Date(similar.first_air_date).getFullYear() : '—'}</SimilarMeta>
            </SimilarInfo>
          </SimilarCard>
        )) : (
          <Placeholder>{t('rightPanel.noSimilar')}</Placeholder>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>{t('rightPanel.reviews')}</SectionTitle>
          <SmallText>{t('rightPanel.all')} ({currentComments.length})</SmallText>
        </SectionHeader>
        {currentComments.length > 0 ? currentComments.map(comment => (
          <ReviewItem key={comment.id}>
            <ReviewHeader>
              <ReviewAuthorWrapper>
                <ReviewAvatar src={comment.userAvatar || comment.user_avatar || '/assets/avatar-placeholder.png'} alt={comment.userName || comment.user_name || t('rightPanel.user')} />
                <ReviewMeta>
                  <ReviewName>{comment.userName || comment.user_name || t('rightPanel.user')}</ReviewName>
                  <ReviewDate>{(comment.createdAt || comment.created_at) ? new Date(comment.createdAt || comment.created_at).toLocaleDateString() : '—'}</ReviewDate>
                </ReviewMeta>
              </ReviewAuthorWrapper>
              <ReviewRating>{comment.rating ? `${comment.rating}/10` : '—'}</ReviewRating>
            </ReviewHeader>
            <div style={{ color: 'rgba(255, 255, 255, 0.72)', fontSize: 13, lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{comment.text || t('rightPanel.noText')}</div>
          </ReviewItem>
        )) : (
          <Placeholder>{t('rightPanel.noReviews')}</Placeholder>
        )}
      </Section>

      {showShareModal && createPortal(
        <RatingModalOverlay onClick={handleCloseShareModal}>
          <div style={{ background: '#18191B', borderRadius: 16, width: 'min(92vw, 400px)', maxHeight: '70vh', overflow: 'hidden', color: '#fff' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #2A2D30', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t('rightPanel.shareProject')}</h3>
              <button onClick={handleCloseShareModal} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 8 }}><X size={20} /></button>
            </div>
            <div style={{ padding: '16px 20px 20px', maxHeight: 'calc(70vh - 74px)', overflowY: 'auto' }}>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
                <input type="text" placeholder={t('rightPanel.searchFriends')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: '100%', padding: '10px 16px 10px 40px', borderRadius: 24, border: '2px solid #2A2D30', background: '#232629', color: '#F5F5F5', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {friends.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()) || f.username.toLowerCase().includes(searchQuery.toLowerCase())).map(friend => (
                <div key={friend.id} onClick={() => handleSelectFriend(friend)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, borderRadius: 12, cursor: 'pointer', background: 'rgba(255,255,255,0.04)', marginBottom: 8 }}>
                  <img src={friend.avatar} alt={friend.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{friend.name}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>@{friend.username}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </RatingModalOverlay>,
        document.body
      )}

      {isReviewsModalOpen ? createPortal(
        <RatingModalOverlay onClick={() => setIsReviewsModalOpen(false)}>
          <div style={{ background: '#18191B', borderRadius: 16, width: 'min(92vw, 560px)', maxHeight: '80vh', overflow: 'hidden', color: '#F5F5F5' }} onClick={(event) => event.stopPropagation()}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #2A2D30', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t('rightPanel.reviews')}</h3>
              <button onClick={() => setIsReviewsModalOpen(false)} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}>{t('rightPanel.close')}</button>
            </div>
            <div style={{ padding: '16px 20px 20px', maxHeight: 'calc(80vh - 74px)', overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <input placeholder={t('rightPanel.writeReview')} value={newReviewText} onChange={(e) => setNewReviewText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitReview(); }} style={{ flex: 1, borderRadius: 24, padding: '11px 14px', border: '2px solid #2A2D30', background: '#232629', color: '#F5F5F5', fontSize: 14, outline: 'none' }} />
                <button disabled={!newReviewText.trim() || reviewSubmitting} onClick={handleSubmitReview} style={{ border: 'none', borderRadius: 24, padding: '11px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: !newReviewText.trim() ? '#999' : '#FFFFFF', color: '#101113' }}>{t(reviewSubmitting ? 'ux.sending' : 'rightPanel.send')}</button>
              </div>
              {draftSaved && <p role="status">{t('ux.draftSaved')}</p>}
              {reviewError && <p role="alert">{reviewError}</p>}
              <CommentList category={itemCategory} id={apiId}/>
            </div>
          </div>
        </RatingModalOverlay>,
        document.body
      ) : null}
    </PanelContainer>
  );
};

export default DetailRightPanel;
